import {Platform} from 'react-native';
import Toast from 'react-native-toast-message';

import {googlePayService} from '../../../components/payments/till/PaymentMethodSelector/googlePay/googlePayService';
import type {GooglePayPayment} from '../../../components/payments/till/PaymentMethodSelector/googlePay/types';
import type {
  GooglePayAndroidConfig,
  GooglePayWebConfig,
} from '../../../components/payments/till/PaymentMethodSelector/types';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import type {CartStore} from '../../../stores/cartStore';
import {logger, serializeError} from '../../../utils/logger';

import {createGooglePayPaymentHandler} from './useGooglePayPaymentHandler';

export interface CreateGooglePayCheckoutConfigsParams {
  cartStore: CartStore;
  /** Google Pay service ID from usePaymentServices */
  googlePayServiceId: string | null;
  /** Log tag for identifying source screen */
  logTag?: string;
}

export interface GooglePayCheckoutConfigs {
  /** Android Google Pay config (undefined on non-Android or if no service ID) */
  googlePayConfig: GooglePayAndroidConfig | undefined;
  /** Web Google Pay config (undefined on non-web or if no service ID) */
  googlePayWebConfig: GooglePayWebConfig | undefined;
}

/**
 * Creates Google Pay checkout configurations for both web and Android.
 * Mirrors createApplePayCheckoutConfigs.
 *
 * On web the Google Pay button hands back a raw GooglePayPayment, so this
 * helper builds the single_use_payment_token client-side (via googlePayService)
 * before posting to checkout. On Android the token is already built inside
 * useGooglePayNative, which calls the handler directly.
 *
 * For gift card split payments, passes remainingBalanceCents so the Google Pay
 * payment sheet shows the correct remaining amount instead of the full cart total.
 */
export function createGooglePayCheckoutConfigs({
  cartStore,
  googlePayServiceId,
  logTag = 'GooglePayCheckout',
}: CreateGooglePayCheckoutConfigsParams): GooglePayCheckoutConfigs {
  const handleCancel = () => {
    logger.info(logTag, 'Google Pay payment cancelled');
  };

  const handleError = (error: Error) => {
    logger.error(logTag, 'Google Pay error', serializeError(error));
    Toast.show({
      type: ERROR_MESSAGE,
      text1: error.message || 'Google Pay error occurred',
    });
  };

  const remainingBalanceCents = cartStore.isGiftCardsBeingUsed
    ? cartStore.remainingBalanceAfterGiftCardsCents
    : undefined;

  // Web config — serviceId-based. The web button returns a raw GooglePayPayment,
  // so build the single_use_payment_token here before reusing the shared handler.
  // Platform gate only (DIS-1515): visibility is enforced by useGooglePayAvailability
  // (isReadyToPay). Desktop with this config still cannot show GP — the probe fails
  // closed and PaymentMethodSelector.web / CheckoutActionButton.web fall back to card.
  let googlePayWebConfig: GooglePayWebConfig | undefined;
  if (Platform.OS === 'web' && googlePayServiceId) {
    const webHandler = createGooglePayPaymentHandler({cartStore, logTag, source: 'web'});
    googlePayWebConfig = {
      serviceId: googlePayServiceId,
      remainingBalanceCents,
      onPaymentAuthorized: async (payment: GooglePayPayment) => {
        const token = googlePayService.buildPaymentToken(payment, googlePayServiceId);
        return webHandler(token);
      },
      onCancel: handleCancel,
      onError: handleError,
    };
  }

  if (Platform.OS !== 'android' || !googlePayServiceId) {
    return {googlePayConfig: undefined, googlePayWebConfig};
  }

  const googlePayConfig: GooglePayAndroidConfig = {
    serviceId: googlePayServiceId,
    remainingBalanceCents,
    onPaymentAuthorized: createGooglePayPaymentHandler({
      cartStore,
      logTag,
      source: 'android',
    }),
    onCancel: handleCancel,
    onError: handleError,
  };

  return {googlePayConfig, googlePayWebConfig};
}
